import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface ConfiguracionNotificaciones {
  notifica_cita_asignada: boolean;
  notifica_cita_trabajador_asignada: boolean;
  notifica_cita_cancelada: boolean;
  notifica_cita_trabajador_cancelada: boolean;
  notifica_cita_reprogramada: boolean;
  notifica_cita_trabajador_reprogramada: boolean;
  notifica_bono_comprado: boolean;
  notifica_ausencia_solicitada: boolean;
  notifica_ausencia_respondida: boolean;
}

type CampoNotificacion = keyof ConfiguracionNotificaciones;

const DEFAULTS: ConfiguracionNotificaciones = {
  notifica_cita_asignada: true,
  notifica_cita_trabajador_asignada: true,
  notifica_cita_cancelada: true,
  notifica_cita_trabajador_cancelada: true,
  notifica_cita_reprogramada: true,
  notifica_cita_trabajador_reprogramada: true,
  notifica_bono_comprado: true,
  notifica_ausencia_solicitada: true,
  notifica_ausencia_respondida: true,
};

const CAMPOS: CampoNotificacion[] = [
  'notifica_cita_asignada',
  'notifica_cita_trabajador_asignada',
  'notifica_cita_cancelada',
  'notifica_cita_trabajador_cancelada',
  'notifica_cita_reprogramada',
  'notifica_cita_trabajador_reprogramada',
  'notifica_bono_comprado',
  'notifica_ausencia_solicitada',
  'notifica_ausencia_respondida',
];

/**
 * Preferencias de notificaciones internas del usuario logueado.
 * Lectura: vista_configuracion_notificaciones. Escritura: la tabla base
 * configuracion_notificaciones (la vista no es editable, hace join con
 * auth.users).
 *
 * Sirve tanto para el panel de empresa como el de trabajador: la tabla y la
 * lógica de carga/guardado son idénticas; qué campos se muestran/editan en
 * cada pantalla es decisión de la UI, no de este hook.
 *
 * Si el usuario todavía no tiene fila (primer acceso), se muestran los
 * valores por defecto (todo activado) y no se crea nada hasta que el
 * usuario cambie algún toggle — el primer updatePreference() hace upsert.
 */
export function useConfiguracionNotificaciones() {
  const [config, setConfig] = useState<ConfiguracionNotificaciones>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeRef = useRef(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      if (activeRef.current) {
        setConfig(DEFAULTS);
        setLoading(false);
      }
      return;
    }

    const { data, error: err } = await supabase
      .from('vista_configuracion_notificaciones')
      .select(CAMPOS.join(', '))
      .eq('id_usuario', user.id)
      .maybeSingle();

    if (!activeRef.current) return;

    if (err) {
      console.error('useConfiguracionNotificaciones (cargar):', err);
      setError(err.message);
      setConfig(DEFAULTS);
    } else if (data) {
      setConfig(data as unknown as ConfiguracionNotificaciones);
    } else {
      // Sin fila todavía: se queda con los valores por defecto (todo TRUE).
      setConfig(DEFAULTS);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    activeRef.current = true;
    cargar();
    return () => {
      activeRef.current = false;
    };
  }, [cargar]);

  /**
   * Cambia un único campo y lo persiste (upsert: crea la fila si es la
   * primera vez). Actualiza en optimista y revierte si falla el guardado.
   */
  const updatePreference = useCallback(
    async (campo: CampoNotificacion, valor: boolean) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        return { ok: false as const, error: 'No hay sesión activa' };
      }

      const anterior = config[campo];
      setConfig((prev) => ({ ...prev, [campo]: valor }));
      setSaving(true);
      setError(null);

      const { error: err } = await supabase
        .from('configuracion_notificaciones')
        .upsert({ id_usuario: user.id, [campo]: valor }, { onConflict: 'id_usuario' });

      setSaving(false);

      if (err) {
        console.error('useConfiguracionNotificaciones (updatePreference):', err);
        setConfig((prev) => ({ ...prev, [campo]: anterior }));
        setError(err.message);
        return { ok: false as const, error: err.message };
      }

      return { ok: true as const };
    },
    [config]
  );

  /**
   * Guarda varios campos a la vez en un único upsert (para diálogos con
   * botón "Guardar" que aplican los cambios en lote, en vez de por toggle).
   */
  const updateAll = useCallback(
    async (cambios: Partial<ConfiguracionNotificaciones>) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        return { ok: false as const, error: 'No hay sesión activa' };
      }

      const anterior = config;
      setConfig((prev) => ({ ...prev, ...cambios }));
      setSaving(true);
      setError(null);

      const { error: err } = await supabase
        .from('configuracion_notificaciones')
        .upsert({ id_usuario: user.id, ...cambios }, { onConflict: 'id_usuario' });

      setSaving(false);

      if (err) {
        console.error('useConfiguracionNotificaciones (updateAll):', err);
        setConfig(anterior);
        setError(err.message);
        return { ok: false as const, error: err.message };
      }

      return { ok: true as const };
    },
    [config]
  );

  return {
    config,
    loading,
    saving,
    error,
    updatePreference,
    updateAll,
    recargar: cargar,
  };
}